import { IVideoPlayer } from "interfaces/VideoStreamingInterface";
import React, { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player/lazy";
import Loading from "components/loading/loading.component";

const VideoPlayer: React.FC<IVideoPlayer> = ({
  video_url,
  thumbnail,
  className,
  videoStatus,
  onStart,
  onEnd,
  onProgress,
  onPaused,
  onSeek,
  onError,
  videoRef,
  onBuffer,
  isPaused,
  count,
  activeIndex,
  index,
  enableNext,
  description,
  image,
  title,
  subtitle,
  handleSlideBack,
  handleSlideNext,
}) => {
  const [videoPlayerStatus, setVideoPlayerStatus] = useState<{
    playing: boolean;
    visible: boolean;
    alreadyStarted: boolean;
    buffering?: boolean;
    preloaded?: boolean;
    ready?: boolean;
  }>({
    playing: false,
    visible: false,
    alreadyStarted: false,
    buffering: false,
    preloaded: false,
    ready: false,
  });

  const [buffered, setBuffered] = useState(0); // Store buffered percentage
  const [played, setPlayed] = useState(0); // Store played percentage
  const [totalDuration, setTotalDuration] = useState(0); // Store total video duration
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  // Ref for ReactPlayer instance
  const seekBarRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (video_url) {
      setVideoPlayerStatus({
        playing: false,
        visible: false,
        alreadyStarted: false,
        buffering: false,
        preloaded: false,
      });
    }
  }, [video_url]);

  useEffect(() => {
    if (video_url) {
      setVideoPlayerStatus((prev) => ({
        ...prev,
        playing: false,
      }));
    }
  }, [activeIndex]);

  const handleProgress = (state: {
    played: number;
    playedSeconds: number;
    loaded: number;
    loadedSeconds: number;
  }) => {
    if (state.loadedSeconds >= 10 && !videoPlayerStatus.preloaded) {
      setVideoPlayerStatus((prevData) => ({
        ...prevData,
        buffering: false,
        preloaded: true,
        // playing: true,
        playing: prevData.alreadyStarted ? prevData.playing : true,
      }));
    }

    setPlayed((state.playedSeconds * 100) / totalDuration); // Set played percentage
    setBuffered((state.loadedSeconds * 100) / totalDuration); // Set buffered percentage
    onProgress(state);
  };

  const handlePlayPauseClick = () => {
    // Toggle playing state using custom button
    setVideoPlayerStatus((prevData) => ({
      ...prevData,
      playing: !prevData.playing,
      alreadyStarted: true,
      buffering: prevData.playing ? false : true, // Start buffering when play is clicked
    }));
  };

  const handleReady = () => {
    setVideoPlayerStatus((prevData) => ({
      ...prevData,
      buffering: true,
      ready: true,
    }));
  };

  const handlePlay = () => {
    setVideoPlayerStatus((prevData) => ({
      ...prevData,
      buffering: false,
      playing: true,
      ready: true,
    }));

    if (
      !document.fullscreenElement &&
      wrapperRef.current &&
      videoPlayerStatus.ready
    ) {
      wrapperRef.current.requestFullscreen().catch((err) => {
        // console.error("Failed to enter fullscreen mode:", err);
      });
      setIsFullscreen(true);
    }
    // onStart();
  };

  const handlePause = () => {
    setVideoPlayerStatus((prevData) => ({
      ...prevData,
      playing: false,
    }));
    // onPaused();
  };

  // const handleSeekBarClick = (event: React.MouseEvent<HTMLDivElement>) => {
  //   if (!seekBarRef.current || !videoRef.current) return;

  //   const rect = seekBarRef.current.getBoundingClientRect();
  //   const clickX = event.clientX - rect.left;
  //   const newPlayedPercentage = (clickX / rect.width) * 100;
  //   const newTime = (newPlayedPercentage / 100) * totalDuration;

  //   videoRef.current.seekTo(newTime, "seconds");
  //   // setPlayed(newPlayedPercentage);
  //   onSeek?.(newTime);
  // };

  const handleFullScreen = () => {
    if (!videoPlayerStatus.ready) {
      return;
    }

    if (!document.fullscreenElement) {
      setIsFullscreen((prev) => !prev);
      wrapperRef.current?.requestFullscreen().catch((err) => {
        console.error("Failed to enter fullscreen mode:", err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      data-fs={isFullscreen ? "true" : undefined}
      className={`relative overflow-hidden transition-all duration-300
    ${
      isFullscreen &&
      "fixed inset-0 flex items-center justify-center bg-[#000]"
    }`}
    >
      {isFullscreen && (
        <style>{`
          [data-fs="true"] [data-fs-video] {
            position: absolute !important;
            top: 2% !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 8% !important;
            width: 100% !important;
            height: auto !important;
          }
          [data-fs="true"] [data-fs-video] > div {
            width: 100% !important;
            height: 100% !important;
          }
          [data-fs="true"] [data-fs-video] > div > div {
            width: 100% !important;
            height: 100% !important;
          }
          [data-fs="true"] [data-fs-video] video {
            width: 100% !important;
            height: 100% !important;
            object-fit: contain !important;
          }
        `}</style>
      )}
      <div
        className={`flex group ${isFullscreen ? "flex-col items-center justify-center w-full h-full" : "items-center"}`}
      >
        {/* Show loading only when buffering after play */}
        {videoPlayerStatus.buffering && (
          <div className="absolute inset-0 flex justify-center items-center">
            <div className="loader" style={{ color: "white" }}>
              <Loading />
            </div>
          </div>
        )}

        <div className={`flex flex-col ${!isFullscreen ? "w-[276px]" : "w-full h-full items-center justify-center"}`}>
          <div data-fs-video={isFullscreen ? "true" : undefined} className={`relative group ${!isFullscreen ? "w-[276px] overflow-hidden" : ""}`}>
            {isFullscreen && videoPlayerStatus.ready && (
              <div
                className={`absolute top-3 px-2 overflow-x-hidden transition-opacity duration-300 ease-in-out opacity-0 group-hover:opacity-100`}
              >
                <div>
                  <div className="flex items-center gap-4">
                    <img
                      src={image}
                      alt="user-image"
                      className="w-16 h-16 rounded-full"
                    />
                    <div>
                      <p className="text-white font-bold text-xl">{title}</p>
                      <p className="text-white text-lg font-medium">
                        {subtitle}
                      </p>
                    </div>
                  </div>

                  <p className="text-white mt-2 break-words overflow-x-hidden w-full">
                    {description}
                  </p>
                </div>
              </div>
            )}

            <ReactPlayer
              ref={videoRef}
              onClickPreview={handlePlay}
              url={video_url}
              className="!rounded-lg !w-[276px] !h-[430px] [&_video]:!w-full [&_video]:!h-full [&_video]:!object-cover"
              config={{
                file: {
                  attributes: {
                    controlsList: "nodownload",
                    preload: "metadata",
                    disablePictureInPicture: true,
                  },
                },
              }}
              loop={false}
              playIcon={
                <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center">
                  <svg width="18" height="20" viewBox="0 0 18 20" fill="none" className="ml-0.5">
                    <path d="M18 10L0 20V0L18 10Z" fill="#3A3A3A" />
                  </svg>
                </div>
              }
              light={
                !videoPlayerStatus.playing &&
                !videoPlayerStatus.alreadyStarted &&
                (thumbnail as string)
              }
              playing={
                videoPlayerStatus.playing
              }
              controls={videoStatus === "completed" ? true : false}
              width="276px"
              height="430px"
              onReady={handleReady}
              pip={false}
              onEnded={() => {
                setVideoPlayerStatus({
                  playing: false,
                  visible: false,
                  alreadyStarted: false,
                  buffering: false,
                  ready: false,
                });
                onEnd();
                if (!document.fullscreenElement) {
                  return;
                }
                document.exitFullscreen();
              }}
              onDuration={setTotalDuration}
              onBuffer={() => {
                setVideoPlayerStatus((prevData) => ({
                  ...prevData,
                  buffering: true,
                }));
                onBuffer();
              }}
              onBufferEnd={() => {
                setVideoPlayerStatus((prevData) => ({
                  ...prevData,
                  buffering: false,
                }));
              }}
              onProgress={handleProgress}
              onPlay={handlePlay}
              onError={onError}
              onSeek={onSeek}
            />
          </div>

          {isFullscreen && (
            <div
              className={`ml-16 flex justify-center space-x-2 mt-6 mr-[60px] ${
                isFullscreen && "pt-4"
              }`}
            >
              {Array.from(Array(count)).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    activeIndex === index ? "w-6 bg-green" : "w-3 bg-gray"
                  }`}
                ></button>
              ))}
            </div>
          )}
        </div>

        {/* Custom Play/Pause Button */}
        {videoStatus !== "completed" && (
          <div
            className={`absolute px-4 ${
              isFullscreen ? " bottom-[10px] pb-4 w-full" : " bottom-0 pb-2 w-[276px]"
            }`}
          >
            {/* Custom Seekbar */}
            <div
              className="relative h-2 w-full bg-black bg-opacity-50 cursor-pointer rounded-lg transition-opacity duration-300 ease-in-out opacity-0 group-hover:opacity-100"
              ref={seekBarRef}
              // onClick={handleSeekBarClick}
            >
              {/* Buffer progress */}
              <div
                className="absolute top-0 left-0 h-2 bg-darkGray rounded-lg transition-opacity duration-300 ease-in-out opacity-0 group-hover:opacity-100"
                style={{ width: `${buffered}%` }} // Width as buffered percentage
              ></div>

              {/* Playback progress */}
              <div
                className="absolute top-0 left-0 h-2 bg-white rounded-lg transition-opacity duration-300 ease-in-out opacity-0 group-hover:opacity-100"
                style={{ width: `${played}%` }} // Width as played percentage
              ></div>
            </div>

            <div className="w-full flex justify-between items-center py-2 mt-1">
              <div className="cursor-pointer" onClick={handlePlayPauseClick}>
                {videoPlayerStatus.playing ? (
                  <svg
                    fill="white"
                    width="15px"
                    height="15px"
                    viewBox="0 0 300 300"
                    className="drop-shadow-lg"
                  >
                    <path d="M14.22,45.665v186.013c0,25.223,16.711,45.66,37.327,45.66c20.618,0,37.339-20.438,37.339-45.66V45.665 c0-25.211-16.721-45.657-37.339-45.657C30.931,0,14.22,20.454,14.22,45.665z" />
                    <path d="M225.78,0c-20.614,0-37.325,20.446-37.325,45.657V231.67c0,25.223,16.711,45.652,37.325,45.652s37.338-20.43,37.338-45.652 V45.665C263.109,20.454,246.394,0,225.78,0z" />
                  </svg>
                ) : (
                  <svg
                    width="15"
                    height="18"
                    viewBox="0 0 15 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="drop-shadow-lg"
                  >
                    <path
                      d="M15 9.12646L1.44431e-05 17.7867L1.52002e-05 0.46621L15 9.12646Z"
                      fill="white"
                    />
                  </svg>
                )}
              </div>

              <div
                className={`flex items-center ${
                  isFullscreen ? "gap-4" : "gap-2"
                }`}
              >
                {/* Restart Button */}
                <div
                  className="cursor-pointer"
                  onClick={() => videoRef.current?.seekTo(0)}
                >
                  <svg
                    width={16}
                    height={22}
                    viewBox="0 0 16 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="drop-shadow-lg"
                  >
                    <g clipPath="url(#clip0_121_3299)">
                      <path
                        d="M2.28129 16.5133C3.51839 17.8416 5.1934 18.5732 6.99805 18.5732C10.568 18.5732 13.4724 15.639 13.4724 12.0326C13.4724 8.4263 10.5679 5.49212 6.99805 5.49212C6.05178 5.49212 5.13421 5.69742 4.29675 6.08615L6.62541 6.73888C7.298 6.92737 7.69201 7.63105 7.50533 8.31052C7.35004 8.87582 6.84186 9.24613 6.28817 9.24613C6.17632 9.24613 6.0626 9.23113 5.9495 9.19952L0.925988 7.79145C0.603145 7.70095 0.328938 7.48446 0.163807 7.18975C-0.00105667 6.89504 -0.0435238 6.54614 0.0460606 6.21973L1.4626 1.06235C1.64919 0.382883 2.34575 -0.0151517 3.01834 0.173434C3.69094 0.361929 4.08495 1.0656 3.89827 1.74507L3.35737 3.7142C4.49076 3.20751 5.72536 2.93872 6.99796 2.93872C11.9618 2.93872 16 7.01814 16 12.0326C16 17.0471 11.9618 21.1266 6.99805 21.1266C4.52554 21.1266 2.1352 20.0827 0.440428 18.2627C-0.0378914 17.7493 -0.013485 16.9412 0.494965 16.4582C1.00323 15.975 1.80341 15.9993 2.28129 16.5133Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_121_3299)">
                        <rect
                          width={16}
                          height={21}
                          fill="white"
                          transform="matrix(-1 0 0 1 16 0.126465)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </div>

                {/* Custom Fullscreen Button */}
                {activeIndex === index && videoPlayerStatus.ready && (
                  <button
                    // className={`absolute bottom-[17px] right-2 text-white px-2 py-1 rounded `}
                    className=""
                    onClick={handleFullScreen}
                  >
                    {!isFullscreen ? (
                      <svg
                        width="22px"
                        height="22px"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        stroke="#fff"
                      >
                        <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                        <g
                          id="SVGRepo_tracerCarrier"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></g>
                        <g id="SVGRepo_iconCarrier">
                          <path
                            d="M14 10L20 4M20 4H15.5M20 4V8.5M4 4L10 10M4 4V8.5M4 4H8.5M14 14L20 20M20 20V15.5M20 20H15.5M10 14L4 20M4 20H8.5M4 20L4 15.5"
                            stroke="#fff"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          ></path>
                        </g>
                      </svg>
                    ) : (
                      <svg
                        width="24px"
                        height="24px"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        stroke="#fff"
                      >
                        <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                        <g
                          id="SVGRepo_tracerCarrier"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></g>
                        <g id="SVGRepo_iconCarrier">
                          <path
                            d="M14 10L20 4M20 4H15.5M20 4V8.5M4 4L10 10M4 4V8.5M4 4H8.5M14 14L20 20M20 20V15.5M20 20H15.5M10 14L4 20M4 20H8.5M4 20L4 15.5"
                            stroke="#fff"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          ></path>{" "}
                        </g>
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* {isFullscreen && (
        <>
          {count !== (index ?? 0) + 1 ? (
            <button
              className={` bg-green rounded-full p-2 ${
                !enableNext
                  ? " cursor-not-allowed opacity-40"
                  : " cursor-pointer"
              }`}
              onClick={handleSlideNext}
              disabled={!enableNext}
            >
              <img
                src="/assets/svg/next-icon.svg"
                alt="Next"
                className="w-8 h-8"
              />
            </button>
          ) : (
            <button
              className=" bg-green px-4 py-2 rounded-3xl"
              // onClick={() => navigate("/on-boarding/complete")}
            >
              <div className="flex items-center gap-2 text-white">
                <p>Finish</p>
                <img
                  src="/assets/svg/next-icon.svg"
                  alt="Next"
                  className="w-4 h-4"
                />
              </div>
            </button>
          )}
        </>
      )} */}
    </div>
  );
};

export default React.memo(VideoPlayer);
